import {
  CompleteDocumentUploadRequestSchema,
  CreateDocumentUploadRequestSchema,
  DocumentSchema,
  DocumentUploadInstructionSchema,
  type CurrentSession,
  type Document,
} from '@nlc/contracts';

import { apiRequest, ApiError } from './client.js';
import {
  createUploadQueue,
  databaseName,
  deleteUploadDatabases,
  hasUploadInstruction,
  isInProgress,
  uploadRejection,
  UploadRejectedError,
  type QueuedUpload,
  type UploadPatch,
  type UploadPhase,
  type UploadQueue,
  type UploadQueueScope,
} from './upload-queue.js';

/** Automatic retries (reconnect, sign-in) stop after this many failures; a tap still retries. */
export const AUTO_RETRY_LIMIT = 5;

export type UploadEvent =
  | { type: 'changed' }
  | { type: 'progress'; id: string; phase: UploadPhase }
  | { type: 'completed'; id: string; document: Document }
  | { type: 'failed'; id: string; error: string }
  /** The server can never accept this file; it has been removed from the queue. */
  | { type: 'rejected'; id: string; error: string };
export type UploadListener = (event: UploadEvent) => void;

const listeners = new Set<UploadListener>();

/** Progress and queue changes for every scope; returns the unsubscribe. */
export function subscribeUploads(listener: UploadListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(event: UploadEvent): void {
  for (const listener of listeners) listener(event);
}

const queues = new Map<string, UploadQueue>();

export function uploadScopeOf(
  session: Pick<CurrentSession, 'activeOrganizationId' | 'membershipId'>,
): UploadQueueScope {
  return { organizationId: session.activeOrganizationId, membershipId: session.membershipId };
}

/** One queue per organization and membership, so drafts never cross tenants or users. */
export function uploadQueueFor(scope: UploadQueueScope): UploadQueue {
  const name = databaseName(scope);
  let queue = queues.get(name);
  if (!queue) {
    queue = createUploadQueue(scope, { onChange: () => notify({ type: 'changed' }) });
    queues.set(name, queue);
  }
  return queue;
}

/**
 * Every tab of this browser shares the scope's IndexedDB store, so runs are serialized per scope
 * with the Web Locks API: one tab uploads while the others wait, then re-read the store and skip
 * what is already done. Without Web Locks the task simply runs unserialized.
 */
function lockManager(): LockManager | undefined {
  const candidate: unknown = (globalThis as { navigator?: { locks?: unknown } }).navigator?.locks;
  return typeof candidate === 'object' && candidate !== null
    ? (candidate as LockManager)
    : undefined;
}

async function withScopeLock<T>(
  queue: UploadQueue,
  task: (locked: boolean) => Promise<T>,
): Promise<T> {
  const locks = lockManager();
  if (!locks) return task(false);
  return locks.request(databaseName(queue.scope), () => task(true));
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

class UploadStepError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

/** The entry was discarded while its upload was running; stop quietly. */
class DiscardedError extends Error {}

const NO_INSTRUCTION: UploadPatch = {
  documentId: undefined,
  uploadUrl: undefined,
  requiredHeaders: undefined,
};

const SESSION_ENDED = 'Your session ended. Sign in again to finish this upload.';

const configuredApiBase: unknown = import.meta.env.VITE_API_BASE_URL;

/**
 * A stored instruction is only followed when it points somewhere the app would talk to anyway.
 * When the API base is configured as an absolute URL the origin must match it; a relative base
 * means the API sits behind a proxy on this origin and the server may still name its own origin
 * in the instruction, so only the scheme is checked there.
 */
export function isTrustedUploadUrl(uploadUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(uploadUrl);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  if (typeof configuredApiBase !== 'string' || !/^https?:\/\//i.test(configuredApiBase)) {
    return true;
  }
  try {
    return new URL(configuredApiBase).origin === parsed.origin;
  } catch {
    return false;
  }
}

/**
 * Turns a failure into what the user sees and whether the next attempt must start again from
 * step 1. A refused PUT or completion (4xx other than auth) means the server-side record is no
 * longer usable; a dropped connection or a 5xx keeps the instruction so the retry resumes.
 */
function describeFailure(caught: unknown): { message: string; restart: boolean } {
  if (caught instanceof ApiError) {
    if (caught.status === 401) return { message: SESSION_ENDED, restart: false };
    return { message: caught.message, restart: caught.status !== 403 && caught.status < 500 };
  }
  if (caught instanceof UploadStepError) {
    if (caught.status === 401) return { message: SESSION_ENDED, restart: false };
    const refused = caught.status >= 400 && caught.status < 500;
    return { message: caught.message, restart: refused && caught.status !== 403 };
  }
  if (caught instanceof TypeError) {
    return {
      message:
        'No connection. This upload is kept on this device and retried when you are back online.',
      restart: false,
    };
  }
  return {
    message: caught instanceof Error ? caught.message : 'The upload did not complete.',
    restart: false,
  };
}

const inFlight = new Map<string, Promise<Document | undefined>>();

export function isUploadInFlight(id: string): boolean {
  return inFlight.has(id);
}

async function perform(queue: UploadQueue, initial: QueuedUpload): Promise<Document | undefined> {
  let entry = initial;
  const advance = async (patch: UploadPatch): Promise<void> => {
    const updated = await queue.update(entry.id, patch);
    if (!updated) throw new DiscardedError();
    entry = updated;
    notify({ type: 'progress', id: entry.id, phase: entry.phase });
  };

  try {
    if (!hasUploadInstruction(entry) || !isTrustedUploadUrl(entry.uploadUrl)) {
      await advance({ ...NO_INSTRUCTION, phase: 'requesting', lastError: null });
      const request = CreateDocumentUploadRequestSchema.safeParse({
        targetType: entry.targetType,
        targetId: entry.targetId,
        fileName: entry.fileName,
        mimeType: entry.mimeType,
        sizeBytes: entry.blob.size,
      });
      if (!request.success) {
        // Only a row the queue never validated gets here; the message names the real limit.
        throw new UploadRejectedError(uploadRejection(entry) ?? 'This file cannot be uploaded.');
      }
      const instruction = await apiRequest('/documents/uploads', DocumentUploadInstructionSchema, {
        method: 'POST',
        body: JSON.stringify(request.data),
      });
      await advance({
        documentId: instruction.document.id,
        uploadUrl: instruction.uploadUrl,
        requiredHeaders: instruction.requiredHeaders,
      });
    }
    await advance({ phase: 'uploading', lastError: null });
    const { documentId, uploadUrl, requiredHeaders } = entry;
    if (documentId === undefined || uploadUrl === undefined) {
      throw new UploadStepError('The upload instruction is missing.', 0);
    }
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      credentials: 'include',
      headers: requiredHeaders ?? {},
      body: entry.blob,
    });
    if (!put.ok) throw new UploadStepError(`Upload failed (${String(put.status)})`, put.status);
    await advance({ phase: 'completing' });
    const checksumSha256 = await sha256Hex(entry.blob);
    const document = await apiRequest(`/documents/${documentId}/complete`, DocumentSchema, {
      method: 'POST',
      body: JSON.stringify(CompleteDocumentUploadRequestSchema.parse({ checksumSha256 })),
    });
    await queue.remove(entry.id);
    notify({ type: 'completed', id: entry.id, document });
    return document;
  } catch (caught: unknown) {
    if (caught instanceof DiscardedError) return undefined;
    if (caught instanceof UploadRejectedError) {
      await queue.remove(entry.id);
      notify({ type: 'rejected', id: entry.id, error: caught.message });
      return undefined;
    }
    const failure = describeFailure(caught);
    await queue.update(entry.id, {
      ...(failure.restart ? NO_INSTRUCTION : {}),
      phase: 'failed',
      attempts: entry.attempts + 1,
      lastError: failure.message,
    });
    notify({ type: 'failed', id: entry.id, error: failure.message });
    return undefined;
  }
}

/** Takes the stored state of the entry and runs it, unless another tab has it spoken for. */
async function runFresh(
  queue: UploadQueue,
  id: string,
  locked: boolean,
): Promise<Document | undefined> {
  const fresh = await queue.refresh(id);
  if (!fresh.entry) return undefined;
  // Without a lock a row left mid-upload may belong to a live tab; never upload it twice.
  if (!locked && fresh.busyElsewhere) return undefined;
  return perform(queue, fresh.entry);
}

/** Registers a run so the UI and later calls can join it instead of starting a second one. */
function track(
  id: string,
  start: () => Promise<Document | undefined>,
): Promise<Document | undefined> {
  const task = start().finally(() => {
    inFlight.delete(id);
  });
  inFlight.set(id, task);
  return task;
}

/**
 * Runs the three steps for one queued entry: create the document (skipped when an instruction
 * is stored), PUT the content, then complete with the checksum. State is written to the queue
 * after each step so a killed tab resumes where it stopped; the entry is re-read from the store
 * first so an upload finished by another tab is not repeated. Resolves with the document on
 * success and `undefined` when the entry stays queued; it never throws. A second call for an
 * entry already running joins the running attempt.
 */
export function runUpload(queue: UploadQueue, entry: QueuedUpload): Promise<Document | undefined> {
  const running = inFlight.get(entry.id);
  if (running) return running;
  return track(entry.id, () => withScopeLock(queue, (locked) => runFresh(queue, entry.id, locked)));
}

/** Retries a stored entry by id; nothing happens when it has been discarded. */
export async function retryUpload(queue: UploadQueue, id: string): Promise<Document | undefined> {
  const entry = await queue.get(id);
  return entry ? runUpload(queue, entry) : undefined;
}

async function runEach(queue: UploadQueue, limit: number | null): Promise<void> {
  await withScopeLock(queue, async (locked) => {
    await queue.resync();
    await queue.purgeExpired();
    // One at a time: field phones share a thin connection, and order keeps the UI legible.
    for (const entry of queue.snapshot()) {
      if (inFlight.has(entry.id)) continue;
      if (limit !== null && entry.attempts >= limit) continue;
      if (!locked && isInProgress(entry)) continue;
      // The lock is already held here; taking it again per entry would wait on itself.
      await track(entry.id, () => runFresh(queue, entry.id, locked));
    }
  });
}

/** Automatic pass (reconnect, mount): skips entries that have already failed too often. */
export async function retryPendingUploads(queue: UploadQueue): Promise<void> {
  if (!navigator.onLine) return;
  await runEach(queue, AUTO_RETRY_LIMIT);
}

/** Manual "Retry all": every entry, regardless of attempts. */
export function retryAllUploads(queue: UploadQueue): Promise<void> {
  return runEach(queue, null);
}

/** Brings in what other tabs did (finished, discarded, added) without uploading anything. */
export function resyncUploads(queue: UploadQueue): Promise<void> {
  return withScopeLock(queue, async () => {
    await queue.resync();
    await queue.purgeExpired();
  });
}

export function discardUpload(queue: UploadQueue, id: string): Promise<void> {
  return queue.remove(id);
}

/** Sign-out: drop every draft on this device, then forget the queues. Never throws. */
export async function purgeAllUploads(): Promise<void> {
  try {
    await Promise.all([...queues.values()].map((queue) => queue.purgeAll()));
  } catch {
    // The queues swallow their own failures; this guards the sign-out path regardless.
  }
  queues.clear();
  await deleteUploadDatabases();
  notify({ type: 'changed' });
}
